import { useState } from 'react';

/**
 * Persists a page's tile order to localStorage (per-browser) so drag-and-drop reordering
 * survives a refresh. Falls back to `defaultOrder` if nothing saved yet, or if the saved
 * order no longer matches the current set of tile ids (e.g. after a future tile is
 * added/removed on that page).
 */
export function useTileOrder(storageKey: string, defaultOrder: string[]): [string[], (order: string[]) => void, () => void] {
  const [order, setOrder] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved) as string[];
        const sameSet = parsed.length === defaultOrder.length && defaultOrder.every((id) => parsed.includes(id));
        if (sameSet) return parsed;
      }
    } catch {
      // ignore corrupt localStorage value, fall back to default
    }
    return defaultOrder;
  });

  const updateOrder = (newOrder: string[]) => {
    setOrder(newOrder);
    localStorage.setItem(storageKey, JSON.stringify(newOrder));
  };

  const resetOrder = () => {
    setOrder(defaultOrder);
    localStorage.removeItem(storageKey);
  };

  return [order, updateOrder, resetOrder];
}
